import {
  getGridSingleSelectOperators,
  GridColDef,
  GridRenderCellParams,
} from '@components/DataTable';
import { ColumnDefinition, UnfurlItem } from '@lib/external/unfurl';
import { formatColumnValue, readColumnValue, UnfurlColumnValue } from '@lib/external/unfurlColumns';
import css from './MediaDataGrid.module.scss';

/**
 * Backend keys a hand-built column already shows. `thumbnail` sits inside the media name cell;
 * the rest have their own columns with custom cells, filters and the sort presets.
 */
export const CURATED_UNFURL_KEYS: ReadonlySet<string> = new Set([
  'thumbnail',
  'title',
  'duration',
  'publishedAt',
  'isShorts',
  'description',
]);

/** Namespaced so a backend key can never collide with a row field such as `status` or `id`. */
export const unfurlColumnField = (key: string) => `columns.${key}`;

/**
 * Wide enough for a header's text plus its padding, column menu button and a sort arrow, which
 * measure about 7.3px a character and 80px in the picker. With many columns the table can't give
 * any of them more than their minimum, so a narrower one truncates its header.
 */
export const headerMinWidth = (headerName: string) => Math.ceil(headerName.length * 7.5) + 80;

const MIN_WIDTH: Record<string, number> = {
  boolean: 110,
  number: 120,
  ISODateString: 140,
  enum: 130,
  thumbnail: 90,
};

const compareValues = (a: UnfurlColumnValue, b: UnfurlColumnValue): number => {
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/** Rows without a value sort last in either direction, as on the Published column. */
const nullsLast =
  (field: string): GridColDef['sortComparator'] =>
  (a, b, { api }) => {
    if (a === null && b === null) return 0;
    const desc = api.getSortModel().find((item) => item.field === field)?.sort === 'desc';
    if (a === null) return desc ? -1 : 1;
    if (b === null) return desc ? 1 : -1;
    return compareValues(a, b);
  };

const ThumbnailCell = ({ src }: { src: string }) => (
  <div className={css.thumbnailContainer}>
    <img
      src={src}
      alt=""
      loading="lazy"
      className={css.thumbnailImage}
      onError={(event) => {
        event.currentTarget.style.visibility = 'hidden';
      }}
    />
  </div>
);

/** A table column for a field the backend sent and no hand-built column covers. */
export function buildUnfurlColumn(definition: ColumnDefinition): GridColDef {
  const field = unfurlColumnField(definition.key);
  const headerName = definition.name || definition.key;
  // Text reads from the left; short values (dates, Yes/No, numbers) sit centred.
  const align = definition.type === 'string' ? 'left' : 'center';
  const column: GridColDef = {
    field,
    headerName,
    flex: 0.4,
    minWidth: Math.max(MIN_WIDTH[definition.type] ?? 180, headerMinWidth(headerName)),
    align,
    headerAlign: align,
    valueGetter: (_value, row) =>
      readColumnValue(definition, (row as UnfurlItem).columns?.[definition.key]),
    sortComparator: nullsLast(field),
    renderCell: (params: GridRenderCellParams) => {
      const text = formatColumnValue(definition, params.value);
      return (
        <span className={css.unfurlCell} title={params.value === null ? undefined : text}>
          {text}
        </span>
      );
    },
  };

  switch (definition.type) {
    case 'number':
      return { ...column, type: 'number' };
    case 'ISODateString':
      return { ...column, type: 'date' };
    case 'boolean':
      return {
        ...column,
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'is',
        ),
      };
    case 'enum':
      return {
        ...column,
        type: 'singleSelect',
        valueOptions: (definition.choices ?? []).map((choice) => ({
          value: choice.key,
          label: choice.value,
        })),
      };
    case 'thumbnail':
      return {
        ...column,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) =>
          typeof params.value === 'string' ? <ThumbnailCell src={params.value} /> : '—',
      };
    default:
      return { ...column, type: 'string' };
  }
}
